import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { 
  ArrowLeft, CheckCircle2, 
  FileText, ClipboardList, Users, Truck, 
  ShieldAlert, AlertTriangle, CheckSquare, GraduationCap 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- CONTENT DATABASE (Aligned to Actual System Logic) ---
const FEATURES = {
  permits: {
    title: "Digital Permit to Work",
    subtitle: "Flexible high-risk workflows. Launch permits standalone or trigger them directly from daily logs.",
    icon: <FileText size={48} color="#2563eb" />,
    benefits: [
      { title: "Custom Logic Jumps", desc: "Owner-defined questions with smart logic. Answering 'Yes' can reveal specific safety checks or hide irrelevant sections." },
      { title: "Approval Chains", desc: "Define your own hierarchy. Route permits to specific owners or safety managers for digital sign-off." },
      { title: "Integrated Workflows", desc: "Permits can be raised independently or auto-triggered by answers in a Daily Work Log." }
    ]
  },
  "daily-log": {
    title: "Daily Work Logs",
    subtitle: "The full picture of site activity. Custom questions, roster integration, and safety triggers.",
    icon: <ClipboardList size={48} color="#2563eb" />,
    benefits: [
      { title: "Smart Permit Triggers", desc: "Answering 'Yes' to risks (e.g., 'Using Grinding Tools?') automatically opens the required Hot Work Permit." },
      { title: "Roster Integration", desc: "Quickly select workers from your existing roster to track shift length and total manpower." },
      { title: "Responsible Engineer", desc: "Tag the specific Site Owner or Engineer responsible for overseeing the shift's activities." }
    ]
  },
  registry: {
    title: "Worker Registry",
    subtitle: "Centralized worker management. Accounts, competencies, and fast site access.",
    icon: <Users size={48} color="#2563eb" />,
    benefits: [
      { title: "QR Code Entry", desc: "Workers sign in instantly via QR code. Fast, secure, and digital." },
      { title: "Compliance Tracking", desc: "Upload and track certifications, licenses, and induction status for every profile." },
      { title: "Account Management", desc: "Create worker accounts easily and maintain a single source of truth for site access." }
    ]
  },
  assets: {
    title: "Asset Management",
    subtitle: "Ensure equipment compliance. Track documentation, inspections, and service dates.",
    icon: <Truck size={48} color="#2563eb" />,
    benefits: [
      { title: "Document Control", desc: "Upload manuals, insurance, and safety certificates with tracked expiry dates." },
      { title: "Service Scheduling", desc: "Log inspection and service dates. Ensure nothing overdue is used on site." },
      { title: "Status Visibility", desc: "Instantly see if a piece of plant or equipment is compliant and ready for use." }
    ]
  },
  audits: {
    title: "Safety Audits",
    subtitle: "Fully customisable inspections. Define your own questions, observations, and action codes.",
    icon: <ShieldAlert size={48} color="#2563eb" />,
    benefits: [
      { title: "Total Customisation", desc: "Build templates that match your exact site risks. Define specific observation categories and codes." },
      { title: "Manual CAPA Linking", desc: "Raise Corrective Actions separately and link them directly to specific audits for traceability." },
      { title: "Offline Capable", desc: "Perform detailed checks anywhere. Sync data when connectivity returns." }
    ]
  },
  incidents: {
    title: "Incident & Hazard Reporting",
    subtitle: "Capture hazards in seconds. Zero friction, zero logins, maximum data.",
    icon: <AlertTriangle size={48} color="#2563eb" />,
    benefits: [
      { 
        title: "Public QR Reporting", 
        desc: "Anyone can scan a 'Safety Station' QR code to report a hazard in 15 seconds without creating an account." 
      },
      { 
        title: "Anonymous Whistleblowing", 
        desc: "Optional anonymous mode encourages workers to report unsafe practices without fear of reprisal." 
      },
      { 
        title: "Investigation Workflow", 
        desc: "Assign investigators, record witness statements, and track root causes to prevent recurrence." 
      }
    ]
  },
  corrective: {
    title: "Corrective Actions (CAPA)",
    subtitle: "Close the loop. Assign, track, and verify fixes with a complete audit trail.",
    icon: <CheckSquare size={48} color="#2563eb" />,
    benefits: [
      { title: "Assignment Audit Trail", desc: "Track every change—see exactly who assigned the action, to whom, and when it changed." },
      { title: "Contextual Linking", desc: "Actions are tied specifically to an Audit or Incident record for full context." },
      { title: "Close & Verify", desc: "Require photo evidence of the fix before an action can be formally closed and verified." }
    ]
  },
  inductions: {
    title: "Inductions & Training",
    subtitle: "Person-led compliance. Digital attendance records for inductions and toolbox talks.",
    icon: <GraduationCap size={48} color="#2563eb" />,
    benefits: [
      { title: "Sign-on-Glass", desc: "Run person-led inductions and have attendees sign digitally on the trainer’s device." },
      { title: "Toolbox Talks", desc: "Upload training materials (PDFs, PPTs) and capture digital signatures from the team." },
      { title: "Flexible Leadership", desc: "Workflows allow talks to be led by the Site Owner or the Contractor." }
    ]
  }
};

const FeaturePage = ({ type }) => {
  const feature = FEATURES[type];
  const navigate = useNavigate();

  // --- ADDED MOBILE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- EMAIL HANDLER ---
  const handleEmailClick = () => {
    // window.location.href = '/register';
    window.location.href = 'mailto:sales@siteact.app';
  };

  // Theme definition
  const theme = {
    primary: '#3ba9e1', secondary: '#0f172a', navBg: '#132238', navText: '#ffffff',
    bgLight: '#f8fafc', bgWhite: '#ffffff'
  };

  if (!feature) return <div>Feature not found</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar theme={theme} />
      
      {/* HERO SECTION */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '100px 20px 40px' : '140px 20px 80px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          
          <button 
              onClick={() => navigate('/')} 
              style={{ 
                background: 'none', border: 'none', cursor: 'pointer', 
                display: 'inline-flex', alignItems: 'center', gap: '8px', 
                color: '#64748b', marginBottom: '30px', fontWeight: '500'
              }}
            >
              <ArrowLeft size={16} /> Back to Home
          </button>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <div style={{ padding: '16px', background: '#e0f2fe', borderRadius: '50%' }}>
              {feature.icon}
            </div>
          </div>
          
          <h1 style={{ fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: '800', color: theme.secondary, marginBottom: '20px', lineHeight: '1.1' }}>
            {feature.title}
          </h1>
          <p style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', color: '#64748b', maxWidth: '700px', margin: '0 auto 40px', lineHeight: '1.6' }}>
            {feature.subtitle}
          </p>
          
          <button 
            onClick={handleEmailClick} 
            style={{ 
              padding: '16px 32px', borderRadius: '8px', border: 'none', 
              background: theme.primary, color: 'white', fontSize: '1.1rem', fontWeight: 'bold', 
              cursor: 'pointer', boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
              width: isMobile ? '100%' : 'auto' // Full width on mobile
            }}
          > 
            Start Free Trial
          </button>
        </div>
      </div>

      {/* BENEFITS GRID */}
      <div style={{ padding: isMobile ? '40px 20px' : '80px 20px', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>
        <h2 style={{ textAlign: 'center', fontSize: isMobile ? '1.8rem' : '2rem', fontWeight: 'bold', color: theme.secondary, marginBottom: '60px' }}>
          Why teams love our {feature.title}
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
          {feature.benefits.map((benefit, i) => (
            <div key={i} style={{ backgroundColor: 'white', padding: '40px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', boxSizing: 'border-box' }}>
              <div style={{ marginBottom: '20px', color: theme.primary }}>
                <CheckCircle2 size={32} />
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: theme.secondary, marginBottom: '15px' }}>
                {benefit.title}
              </h3>
              <p style={{ color: '#64748b', lineHeight: '1.6' }}>
                {benefit.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA BANNER */}
      <div style={{ padding: isMobile ? '60px 20px' : '80px 20px', backgroundColor: theme.secondary, textAlign: 'center', color: 'white', boxSizing: 'border-box' }}>
        <h2 style={{ fontSize: isMobile ? '1.8rem' : '2rem', fontWeight: 'bold', marginBottom: '20px' }}>Ready to standardise your site?</h2>
        <p style={{ fontSize: '1.1rem', opacity: 0.8, marginBottom: '40px', maxWidth: '600px', margin: '0 auto 40px' }}>
          Get started with {feature.title} today. No credit card required.
        </p>
        <button 
          onClick={handleEmailClick} 
          style={{ 
            padding: '16px 32px', borderRadius: '8px', border: 'none', 
            background: 'white', color: theme.secondary, fontSize: '1.1rem', fontWeight: 'bold', 
            cursor: 'pointer', width: isMobile ? '100%' : 'auto' // Full width on mobile
          }}
        >
          Try It Free
        </button>
      </div>

      <Footer theme={theme} />
    </div>
  );
};

export default FeaturePage;